import { Button, Tabs, Typography } from "@hellocarbo/lasagne";
import Card from "@ui/Card";
import { ArrowRight, Building, Layers, Menu } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { ConsolidatedReview as TConsolidatedReview } from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { formatCategoriesForPieCharts } from "@/utils/consolidation";
import {
  calculateEconomicCarbonIntensity,
  calculateSocialCarbonIntensity,
  getDatetimeYear,
} from "@/utils/misc";

import CarbonIntensity from "./CarbonIntensity";
import ConsolidationPie from "./ConsolidationPie";
import ReviewByCategories from "./ReviewByCategories";
import ReviewByScopes from "./ReviewByScopes";
import ReviewBySubsidiaries from "./ReviewBySubsidiaries";

function Consolidation({
  consolidatedReview,
}: {
  consolidatedReview: TConsolidatedReview;
}) {
  const { t } = useTranslation(["common", "dashboard", "formatter"]);

  if (!consolidatedReview) {
    return (
      <div className="grid place-items-center">
        <p>{t("common:loading")}</p>
      </div>
    );
  }

  const {
    carbon,
    turnover,
    employees,
    review_categories: categoriesData,
    scopes,
    subsidiaries,
    period_start,
    period_end,
  } = consolidatedReview;

  const chartCategories = formatCategoriesForPieCharts(categoriesData);

  const { formattedCarbon: formattedCarbonTotal, unit: totalUnit } =
    formatCarbon(t, carbon);

  return (
    <Card className="gap-x-sm gap-y-md grid grid-cols-1 bg-transparent p-0! lg:grid-cols-[1fr_2fr] lg:bg-white">
      <Card className="border-grayscale-xx-light border lg:shadow-xl">
        <div className="mb-lg">
          <Typography step="2" as="h3" className="mb-2xs">
            {t("dashboard:consolidation.subtitle", {
              year: getDatetimeYear(period_start),
            })}
          </Typography>
          <Typography step="0">
            <Trans
              t={t}
              i18nKey="dashboard:consolidation.dates"
              values={{
                from: t("formatter:date", {
                  value: new Date(period_start),
                }),
                to: t("formatter:date", {
                  value: new Date(period_end),
                }),
              }}
              components={{
                1: <span className="font-bold" />,
              }}
            />
          </Typography>
        </div>
        <div className="gap-x-xl gap-y-md grid grid-cols-[1fr_2fr] lg:flex lg:h-full lg:flex-col">
          <div className="min-h-[250px]">
            <ConsolidationPie
              data={chartCategories}
              title={formattedCarbonTotal}
              titleSubText={t(`common:unit.${totalUnit}`)}
              extraSeriesParams={{
                radius: ["50%", "90%"],
              }}
            />
          </div>
          <div className="gap-y-sm flex flex-col">
            <CarbonIntensity
              title={t("common:carbon_intensity.economic.title")}
              value={
                turnover
                  ? calculateEconomicCarbonIntensity(carbon, turnover)
                  : undefined
              }
              unit={t("common:unit.carbon_intensity.economic")}
              emptyValueMessage={t(
                "common:carbon_intensity.economic.emptyValueMessage"
              )}
            />
            <CarbonIntensity
              title={t("common:carbon_intensity.social.title")}
              value={
                employees
                  ? calculateSocialCarbonIntensity(carbon, employees)
                  : undefined
              }
              unit={t("common:unit.carbon_intensity.social")}
              emptyValueMessage={t(
                "common:carbon_intensity.social.emptyValueMessage"
              )}
            />
          </div>
        </div>
      </Card>
      <Card className="min-h-[500px]">
        <Tabs
          defaultValue="categories"
          extraContent={
            consolidatedReview.id ? (
              <Button
                as="a"
                href={`/reviews/${consolidatedReview.id}`}
                shape="link"
                intent="secondary"
                size="small"
              >
                {t("dashboard:consolidation.seeMyFootprint")}
                <ArrowRight className="size-md" />
              </Button>
            ) : null
          }
          tabs={[
            {
              label: t("dashboard:consolidation.views.categories"),
              value: "categories",
              PrefixIcon: Layers,
              content: <ReviewByCategories categories={chartCategories} />,
            },
            {
              label: t("dashboard:consolidation.views.scopes"),
              value: "scopes",
              PrefixIcon: Building,
              disabled: !scopes,
              content: scopes ? <ReviewByScopes scopes={scopes} /> : null,
            },
            {
              label: t("dashboard:consolidation.views.subsidiaries"),
              value: "subsidiaries",
              PrefixIcon: Menu,
              disabled: !subsidiaries,
              content: subsidiaries ? (
                <ReviewBySubsidiaries
                  subsidiaries={subsidiaries}
                  total={carbon}
                />
              ) : null,
            },
          ]}
        />
      </Card>
    </Card>
  );
}

export default Consolidation;
